import { useEffect, useState } from "react";

/**
 * useReducedMotion - Detects user's prefers-reduced-motion preference.
 *
 * Dashboard Polish v2 (2026-05-19): Codex P2 requires full coverage across
 * every animation system added in this pass (atmospheric bloom, particle
 * trail, step completion bounce + sparkle, level celebration, parallax,
 * stats slot-machine, stats pear pulse). Every motion guard checks this
 * hook; when the user has the OS preference set, the dashboard renders
 * with static counterparts (instant transitions, no bursts, no parallax).
 *
 * Implementation notes:
 * - SSR / no-window safe: returns `false` (no motion preference detected)
 *   when matchMedia is unavailable. Animations render normally; reading
 *   matchMedia on the client kicks in once the component mounts.
 * - Listens to the media query for live preference changes (system toggle
 *   mid-session is rare but free to support).
 * - addEventListener / removeEventListener with the same handler; legacy
 *   Safari fallback to addListener / removeListener if addEventListener
 *   is missing on the MediaQueryList (older WebKit).
 *
 * Usage:
 *   const prefersReducedMotion = useReducedMotion();
 *   if (prefersReducedMotion) {
 *     return <StaticVariant />;
 *   }
 */
const QUERY = "(prefers-reduced-motion: reduce)";

const useReducedMotion = () => {
  // Read the preference synchronously on initial render so reduced-
  // motion users don't see a single-frame flash of the animated
  // version before the post-mount useEffect catches up. The lazy
  // initializer is safe under SSR / no-window environments
  // (returns false when matchMedia is unavailable). Per CodeRabbit
  // finding on PR #327 round 3.
  const [prefersReduced, setPrefersReduced] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mql = window.matchMedia(QUERY);
    // Re-sync in case the preference changed between initial-render
    // synchronous read and effect commit (vanishingly rare but free).
    setPrefersReduced(mql.matches);

    const handler = (event) => {
      setPrefersReduced(event.matches);
    };

    // Modern browsers + recent Safari
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }

    // Legacy Safari (< 14): addListener / removeListener
    if (typeof mql.addListener === "function") {
      mql.addListener(handler);
      return () => mql.removeListener(handler);
    }

    return undefined;
  }, []);

  return prefersReduced;
};

export default useReducedMotion;
